import {
  forwardRef,
  type RefObject,
  Suspense,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { LazyPdfViewer } from '@/modules/projects/components/dialogs/LazyPdfViewer'
import type { PdfViewerHandle } from '@/modules/projects/components/dialogs/PdfViewer'
import { cn } from '@/utils'

export interface StatementPaneSource {
  statementId: number
  fileName: string
  fileUrl: string
  // Only these pages render. Stable per statement: it is a document-effect dependency, so deriving
  // it from the selected row would reload the PDF on every click.
  pageRange: { start: number; end: number } | null
  firstMemberPage: number | null
}

export interface StatementPaneHandle {
  showPage: (statementId: number, page: number | null) => void
}

interface StatementPaneProps {
  sources: StatementPaneSource[]
  activeStatementId: number
  activePage: number | null
}

const Message = ({ children }: { children: string }) => (
  <div className="flex h-full items-center justify-center p-4 text-center text-sm text-muted-foreground">
    {children}
  </div>
)

/**
 * The statement half of the review. A duplicate group's members can live in different uploads, so
 * this keeps one viewer per statement and shows the one the selected row came from.
 *
 * Viewers are never re-pointed at another file: swapping `url` on a live viewer reloads the document
 * under it. Hiding instead of unmounting keeps each document parsed, and its zoom, for the trip back.
 * Scroll is not kept: `display:none` resets it.
 */
export const StatementPane = forwardRef<
  StatementPaneHandle,
  StatementPaneProps
>(({ sources, activeStatementId, activePage }, ref) => {
  const viewerRefs = useRef(
    new Map<number, RefObject<PdfViewerHandle | null>>(),
  )

  const refFor = (statementId: number) => {
    const existing = viewerRefs.current.get(statementId)
    if (existing) return existing
    const created: RefObject<PdfViewerHandle | null> = { current: null }
    viewerRefs.current.set(statementId, created)
    return created
  }

  // A statement's document is only fetched once the user actually looks at it, then kept.
  const [mountedIds, setMountedIds] = useState<number[]>([])
  useEffect(() => {
    setMountedIds((ids) =>
      ids.includes(activeStatementId) ? ids : [...ids, activeStatementId],
    )
  }, [activeStatementId])

  // A pick is a jump request, not state to sync: copies usually share a page, so an effect keyed on
  // the page would sit still. The caller passes statementId because its own selection state has not
  // rendered yet when this runs.
  useImperativeHandle(ref, () => ({
    showPage: (statementId, page) => {
      if (page == null) return
      viewerRefs.current.get(statementId)?.current?.goToPage(page)
    },
  }))

  const active = sources.find(
    (source) => source.statementId === activeStatementId,
  )

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-4 py-1.5 text-xs font-medium text-muted-foreground">
        <span className="truncate">{active?.fileName ?? 'Statement'}</span>
        <span className="shrink-0 tabular-nums">
          {activePage != null ? `Page ${activePage}` : 'Page unknown'}
        </span>
      </div>
      {!active?.fileUrl && (
        <div className="min-h-0 flex-1">
          <Message>No source document is attached to this statement.</Message>
        </div>
      )}
      {sources.map((source) => {
        const isActive = source.statementId === activeStatementId
        if (!source.fileUrl || !mountedIds.includes(source.statementId)) {
          return null
        }

        return (
          <div
            key={source.statementId}
            className={cn('min-h-0 flex-1', !isActive && 'hidden')}
          >
            <Suspense fallback={<Message>Loading the document…</Message>}>
              <LazyPdfViewer
                ref={refFor(source.statementId)}
                url={source.fileUrl}
                // How a viewer that is not mounted yet, or still hidden, lands on its page.
                initialPage={
                  isActive ? (activePage ?? source.firstMemberPage) : null
                }
                pageRange={source.pageRange}
                highlightRange={
                  isActive && activePage != null
                    ? { start: activePage, end: activePage }
                    : null
                }
                // Names the row, not the page: no page here is a duplicated page.
                highlightLabel="Selected row"
              />
            </Suspense>
          </div>
        )
      })}
    </>
  )
})

StatementPane.displayName = 'StatementPane'
